import React, { useState, useContext, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import {
  TextInput,
  TextAreaInput,
  SelectInput,
} from "../../react-hook-form-inputs";
import { PortalizedModalContext } from "./../../PortalizedModal";
import _ from 'underscore.string'
import { renderRailsErrors } from "../../../utils/rails_error_parser";
import {
  BASE_CACHEKEY_MAP,
  addPitchSection,
  updatePitchSection,
  deletePitchSection,
} from "./../queries";
import {
  IconClose,
  IconEdit,
  highlightIcon,
} from './../../../assets/images/icons'

const HighlightSchema = Yup.object().shape({
  highlight_title: Yup.string().required("Title is required.").max(40, 'Title max length is 40 characters'),
  highlight_description: Yup.string().required("Description is required.").max(60, 'Description max length is 60 characters'),
  highlight_type: Yup.string().required("Description type is required."),
});

const Highlight = ({highlight, highlightEditHandler, highlightDeleteHandler})  =>{
  return(
    <div key={highlight.id} className="single-existing-highlight mb-3">
      <div className="single-existing-pitch-content">
        <div className="existing-highlight-icon-box fs-4">
          <img src={highlightIcon(highlight.highlight_type)} alt={'icon'}/>
        </div>
        <div className="existing-highlight-content-box">
          <div className="inner1">
            <p className="existing-highlight-title poppins mb-0">
              {_.prune(highlight.title, 25, '...')}
            </p>
            <span className="existing-highlight-subtitle poppins fs-10">
              {_.prune(highlight.description, 25, '...')}
            </span>
          </div>
        </div>
      </div>
      <div className="existing-pitch-actions">
          <img
            onClick={() => highlightEditHandler(highlight) }
            src={IconEdit}
            alt="edit icon"
          />
          <img
            onClick={() => highlightDeleteHandler(highlight.id)}
            src={IconClose}
            alt="close icon"
          />
        </div>
    </div>
  )
}

const Highlights = ({pitch, pitchableType, pitchableId }) => {
  const deleteHighlightModal = useContext( PortalizedModalContext );
  const highlights = pitch.highlights || [] ;
  const [highlightForEdit, setHighlightForEdit] = useState(null);
  const queryClient = useQueryClient();

  const methods = useForm({
    resolver: yupResolver(HighlightSchema),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      highlight_title: "",
      highlight_description: "",
      highlight_type: "",
    },
  });

  useEffect(()=>{
    if(highlightForEdit){
      methods.setValue('highlight_title',highlightForEdit.title)
      methods.setValue('highlight_description', highlightForEdit.description)
      methods.setValue('highlight_type',highlightForEdit.highlight_type)
    }else{
      methods.reset()
    }
  },[highlightForEdit, methods])

  const deleteHighlight = useMutation(deletePitchSection, {
    onSuccess: () => {
      queryClient.invalidateQueries([BASE_CACHEKEY_MAP[pitchableType], pitchableId]);
      toast.success(`Highlight deleted successfully.`);
    },
    onError: (error) => {
      console.log(error);
    }, 
  });

  function highlightDeleteHandler (id){
    deleteHighlightModal.setModalContent(
      `Delete Highlight`,
      <>
        <div className="px-3 pt-3">
          <p>Are you sure you want to delete the highlight?</p>
        </div>
        <div className="alert-modal-footer pb-3">
          <button className="zeitung-mono weight-300 mr-3 btn-black-outline" onClick={() => deleteHighlightModal.closeModal()}>Cancel</button>
          <button className="zeitung-mono weight-300 btn-black"
            onClick={() => {
              deleteHighlight.mutate({
                section_id: id,
                type: "highlight",
              });
              if(id === highlightForEdit?.id)
                setHighlightForEdit(null)
              deleteHighlightModal.closeModal()
            }}
          >Confirm</button>
        </div>  
      </>
    );
    deleteHighlightModal.openModal();
  }
  const saveHighlight = useMutation(
    highlightForEdit ? updatePitchSection : addPitchSection,
    {
      onSuccess: (values) => {
        methods.reset()
        highlightForEdit && setHighlightForEdit(null)
        queryClient.invalidateQueries([BASE_CACHEKEY_MAP[pitchableType], pitchableId]);
        
        toast.success(`Highlight ${highlightForEdit ? 'updated' : 'added'}  successfully.`);
      },
      onError: (error) => {
        renderRailsErrors(error)
      },
    }
  );

  const onSubmit = (values) => {
    let id = highlightForEdit?.id ?? pitchableId;
    saveHighlight.mutate({
      pitchable_type: pitchableType,
      pitchable_id: id,
      values: {
        type: "highlight",
        pitch_section: {
          title: values.highlight_title,
          description: values.highlight_description,
          highlight_type: values.highlight_type,
        },
      },
    });
  };

  return (
    <div className="highlights-maker">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="add-highlights mb-4"
        >
          <h6 className="fs-10 montserrat weight-600">
            Add new highlight
          </h6>
          <TextInput name="highlight_title" placeholder="Title" type="text" />

          <TextAreaInput
            name="highlight_description"
            placeholder="Description"
            hideExternalPlugins={false} containerStyle={{ height: "400px"}}
          />

          <SelectInput
            name="highlight_type"
            options={["monetary", "award", "technology", "team", "traction"]}
            placeholder="Type"
          />

          <button type="submit" className="mx-auto btn btn-outline-dark rounded-0 poppins">
            {highlightForEdit ? "UPADTE HIGHLIGHT" : "ADD HIGHLIGHT"}
          </button>

          {highlightForEdit && (
            <button
              onClick={() => {
                setHighlightForEdit(null);
              }}
              type="button"
              className="mx-auto btn btn-outline-dark rounded-0 w-25 ms-3"
            >
              CANCEL
            </button>
          )}
        </form>
      </FormProvider>
      {highlights.length > 0 &&
        <div className="existing-highlights">
          <h6 className="fs-10 montserrat weight-600">
            Existing highlights
          </h6>
          {highlights.map((highlight) => (
            <Highlight
              key={highlight.id}
              highlight={highlight}
              highlightEditHandler={setHighlightForEdit} 
              highlightDeleteHandler={highlightDeleteHandler}
            />
          ))}
        </div> }
    </div>
  );
};

Highlights.defaultProps = {};

Highlights.propTypes = {
  pitch: PropTypes.object.isRequired,
  pitchableType: PropTypes.string.isRequired,
  pitchableId: PropTypes.string.isRequired,
};

export default Highlights;
